// Loan Recovery voucher (LRC) — a staff member pays a Staff Loan (LON) or a
// Salary Advance (ADV) back in cash or bank, outside payroll.
//
// The voucher posts Dr Cash/Bank, Cr Staff Account (subTransactionHelper) and
// lowers the balance of the loans / advances it is allocated to:
//   recoveredAmount + amount, outstandingAmount − amount,
//   voucherRecoveredAmount + amount (the part paid by voucher, for reports),
//   balance 0 → loan Closed / advance Recovered (payroll stops deducting it).
// Installments already paid are not touched: a part payment keeps the same
// monthly installment and the loan simply finishes sooner (payroll deducts
// at most what is left).
//
// Only a POSTED voucher counts. Every save / status change / delete calls
// syncRecoveryEffects(old, new): it applies the difference between what the
// old and the new voucher count against each loan / advance, so editing,
// cancelling or deleting puts the balances back exactly.
//
// Firebase cost: 2 small queries to list one staff's open loans / advances;
// 1 read + 1 write per loan / advance touched when a voucher is saved.

import { collection, doc, getDoc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';

export const LOAN_RECOVERY_CODE = 'LRC';
export const LOAN_RECOVERY_MODULE_ID = 'salary_loan_recovery';

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const outstandingOf = (d) => Number(d.outstandingAmount ?? d.totalAmount) || 0;

/** Open loans (Active) and advances (Issued) of one staff, oldest first. */
export const loadOpenBalancesForStaff = async (companyId, staffId) => {
  if (!companyId || !staffId) return [];
  const col = collection(db, 'companies', companyId, 'transactions');
  const [advSnap, lonSnap] = await Promise.all([
    getDocs(query(col, where('tranTypeCode', '==', 'ADV'), where('partyId', '==', staffId), where('status', '==', 'Issued'))),
    getDocs(query(col, where('tranTypeCode', '==', 'LON'), where('partyId', '==', staffId), where('status', '==', 'Active'))),
  ]);
  const rows = [];
  [...lonSnap.docs, ...advSnap.docs].forEach((d) => {
    const x = d.data();
    const outstanding = r2(outstandingOf(x));
    if (outstanding <= 0) return;
    rows.push({
      transactionId: d.id,
      tranTypeCode: x.tranTypeCode,
      docNumber: x.docNumber || '',
      docDate: x.docDate || '',
      totalAmount: r2(x.totalAmount),
      outstanding,
    });
  });
  return rows.sort((a, b) => (a.docDate || '').localeCompare(b.docDate || '') || (a.docNumber || '').localeCompare(b.docNumber || ''));
};

/** What a voucher counts against each loan / advance: { id: amount } (posted only). */
export const countedAllocations = (voucher) => {
  const out = {};
  if (!voucher || voucher.status !== 'Posted') return out;
  (voucher.allocations || []).forEach((a) => {
    const amt = r2(a.amount);
    if (a.transactionId && amt > 0) out[a.transactionId] = r2((out[a.transactionId] || 0) + amt);
  });
  return out;
};

/**
 * Check a voucher's allocations against the CURRENT balances (fresh reads).
 * `oldVoucher` = the saved version when editing (its own counted amounts are
 * available again). Returns '' when fine, else a message for the user.
 */
export const checkAllocations = async (companyId, voucher, oldVoucher = null) => {
  const allocs = (voucher.allocations || []).filter((a) => r2(a.amount) > 0);
  if (allocs.length === 0) return 'Enter the amount received against at least one loan or advance.';
  const prev = countedAllocations(oldVoucher);
  for (const a of allocs) {
    const snap = await getDoc(doc(db, 'companies', companyId, 'transactions', a.transactionId));
    if (!snap.exists()) return `${a.docNumber || 'A loan / advance'} no longer exists.`;
    const x = snap.data();
    if (x.partyId !== voucher.partyId) return `${a.docNumber} belongs to another staff member.`;
    const okStatus = x.tranTypeCode === 'LON'
      ? (x.status === 'Active' || (x.status === 'Closed' && prev[a.transactionId]))
      : (x.status === 'Issued' || (x.status === 'Recovered' && prev[a.transactionId]));
    if (!okStatus) return `${a.docNumber} is ${x.status} — nothing left to recover on it.`;
    const available = r2(outstandingOf(x) + (prev[a.transactionId] || 0));
    if (r2(a.amount) > available + 0.01) {
      return `${a.docNumber}: only ${available.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} is left — the amount can't be more than that.`;
    }
  }
  return '';
};

/**
 * Bring the loans / advances in line with a voucher change.
 *   create : syncRecoveryEffects(id, null, saved)
 *   edit   : syncRecoveryEffects(id, before, after)
 *   status : syncRecoveryEffects(id, before, { ...before, status })
 *   delete : syncRecoveryEffects(id, before, null)
 */
export const syncRecoveryEffects = async (companyId, oldVoucher, newVoucher) => {
  const before = countedAllocations(oldVoucher);
  const after = countedAllocations(newVoucher);
  const ids = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  const batch = writeBatch(db);
  let n = 0;
  for (const id of ids) {
    const delta = r2((after[id] || 0) - (before[id] || 0));
    if (Math.abs(delta) < 0.005) continue;
    const ref = doc(db, 'companies', companyId, 'transactions', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const x = snap.data();
    const total = r2(x.totalAmount);
    const recovered = Math.min(total, Math.max(0, r2((Number(x.recoveredAmount) || 0) + delta)));
    const byVoucher = Math.max(0, r2((Number(x.voucherRecoveredAmount) || 0) + delta));
    const outstanding = Math.max(0, r2(total - recovered));
    let status = x.status;
    if (x.tranTypeCode === 'LON') {
      if (outstanding <= 0.01 && status === 'Active') status = 'Closed';
      else if (outstanding > 0.01 && status === 'Closed') status = 'Active';
    } else if (x.tranTypeCode === 'ADV') {
      if (outstanding <= 0.01 && status === 'Issued') status = 'Recovered';
      else if (outstanding > 0.01 && status === 'Recovered') status = 'Issued';
    }
    batch.update(ref, {
      recoveredAmount: recovered,
      outstandingAmount: outstanding,
      voucherRecoveredAmount: byVoucher,
      status,
    });
    n += 1;
  }
  if (n > 0) await batch.commit();
  return n;
};

/**
 * For the "Recalculate recovery" repair on a loan / advance: what posted Loan
 * Recovery vouchers count against it. Returns { amount }.
 */
export const voucherRecoveriesFor = async (companyId, transactionId) => {
  const snap = await getDocs(query(
    collection(db, 'companies', companyId, 'transactions'),
    where('tranTypeCode', '==', LOAN_RECOVERY_CODE)
  ));
  let amount = 0;
  snap.docs.forEach((d) => {
    const v = d.data();
    amount += countedAllocations(v)[transactionId] || 0;
  });
  return { amount: r2(amount) };
};

/**
 * Payroll: lines whose loan / advance deduction is more than what is still
 * left on those loans / advances (e.g. part was paid back by voucher after the
 * payroll was prepared). Returns [{ staffName, kind, deduction, left }].
 */
export const deductionsAboveBalance = async (companyId, payrollLines) => {
  const out = [];
  const cache = {};
  const left = async (refs) => {
    let sum = 0;
    for (const ref of (refs || [])) {
      if (!ref || !ref.transactionId) continue;
      if (!(ref.transactionId in cache)) {
        const snap = await getDoc(doc(db, 'companies', companyId, 'transactions', ref.transactionId));
        const x = snap.exists() ? snap.data() : null;
        const open = x && ((x.tranTypeCode === 'LON' && x.status === 'Active') || (x.tranTypeCode === 'ADV' && x.status === 'Issued'));
        cache[ref.transactionId] = open ? outstandingOf(x) : 0;
      }
      sum += cache[ref.transactionId];
    }
    return r2(sum);
  };
  for (const l of (payrollLines || [])) {
    const loan = r2(l.loanInstallment);
    if (loan > 0) {
      const have = await left(l.loanRefs);
      if (loan > have + 0.01) out.push({ staffName: l.staffName || '', kind: 'Loan', deduction: loan, left: have });
    }
    const adv = r2(l.advanceRecovery);
    if (adv > 0) {
      const have = await left(l.advanceRefs);
      if (adv > have + 0.01) out.push({ staffName: l.staffName || '', kind: 'Advance', deduction: adv, left: have });
    }
  }
  return out;
};
